/**
 * BadgePage.jsx — Página pública de verificación de insignia
 * Ruta: /badge/:verificationId
 * T30-T32: noindex, sin auth, muestra datos públicos.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import BadgeVisual from '../components/programs/BadgeVisual';
import { Award, AlertCircle, Loader } from 'lucide-react';
import apiClient from '../services/apiClient';

export default function BadgePage() {
  const { verificationId } = useParams();
  const [state, setState] = useState('loading'); // 'loading' | 'found' | 'notfound' | 'revoked'
  const [badge, setBadge] = useState(null);

  useEffect(() => {
    if (!verificationId) {
      setState('notfound');
      return;
    }
    apiClient.get(`/badges/public/${verificationId}`)
      .then(res => {
        setBadge(res.data.badge);
        setState('found');
      })
      .catch(err => {
        if (err.response?.status === 410) {
          setState('revoked');
        } else {
          setState('notfound');
        }
      });
  }, [verificationId]);

  // OG tags dinámicas: se actualizan cuando el badge carga (útil para bots que ejecutan JS y crawlers)
  const ogTitle = badge
    ? `${badge.displayNameSnapshot} obtuvo ${badge.badgeLabelSnapshot}`
    : 'Verificación de insignia — EduTech Pulse Lab';
  const ogDescription = badge
    ? [badge.programTitleSnapshot, badge.claimedAt ? `Emitida el ${new Date(badge.claimedAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}` : '', 'Credencial digital verificable — EduTech Pulse Lab'].filter(Boolean).join(' · ')
    : 'Página de verificación pública de insignia digital EduTech Pulse Lab.';

  return (
    <>
      <SEOHead
        title={ogTitle}
        description={ogDescription}
        robots="noindex, nofollow"
        canonicalUrl={`/badge/${verificationId}`}
      />
      <Navbar />
      <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center px-6 py-16">

        {/* Cargando */}
        {state === 'loading' && (
          <div className="flex flex-col items-center gap-3 text-slate-500">
            <Loader className="w-8 h-8 animate-spin text-cyan-500" />
            <p className="text-sm">Verificando insignia...</p>
          </div>
        )}

        {/* Encontrada */}
        {state === 'found' && badge && (
          <div className="w-full max-w-sm space-y-6 text-center">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full mb-3">
                <Award className="w-3.5 h-3.5" />
                Insignia verificada
              </div>
              <h1 className="text-xl font-bold text-slate-900 leading-snug">
                {badge.badgeLabelSnapshot}
              </h1>
              <p className="text-sm text-slate-500 mt-1">{badge.programTitleSnapshot}</p>
            </div>

            <div className="flex justify-center">
              <BadgeVisual badge={badge} />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-left space-y-1">
              <p className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">Titular:</span> {badge.displayNameSnapshot}
              </p>
              {badge.claimedAt && (
                <p className="text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">Emitida:</span>{' '}
                  {new Date(badge.claimedAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
              <p className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">ID verificación:</span>{' '}
                <span className="break-all">{badge.verificationId}</span>
              </p>
            </div>

            <Link
              to="/programas"
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg text-sm transition-colors"
            >
              Ver programas disponibles
            </Link>
          </div>
        )}

        {/* Revocada */}
        {state === 'revoked' && (
          <div className="flex flex-col items-center gap-4 text-center max-w-sm">
            <AlertCircle className="w-10 h-10 text-amber-500" />
            <h1 className="text-lg font-bold text-slate-800">Esta insignia ha sido revocada</h1>
            <p className="text-sm text-slate-600 leading-relaxed">
              La insignia asociada a este enlace ya no está activa. Si crees que es un error, contacta con soporte.
            </p>
            <Link to="/" className="text-sm text-cyan-600 hover:underline">Volver al inicio</Link>
          </div>
        )}

        {/* No encontrada */}
        {state === 'notfound' && (
          <div className="flex flex-col items-center gap-4 text-center max-w-sm">
            <AlertCircle className="w-10 h-10 text-slate-400" />
            <h1 className="text-lg font-bold text-slate-800">Insignia no encontrada</h1>
            <p className="text-sm text-slate-600 leading-relaxed">
              No existe ninguna insignia con este identificador. Verifica el enlace y vuelve a intentarlo.
            </p>
            <Link to="/" className="text-sm text-cyan-600 hover:underline">Volver al inicio</Link>
          </div>
        )}
      </main>
    </>
  );
}
